import { isFlexible, flexibleWindowOver, nextArrival } from "../retreatAvailability";
// ─── What a school's cards say about price, dates and length (Phase 2 · T1) ───
// Students should be able to shortlist from the browse page without opening
// every school. Everything here is derived from live retreats and classes —
// schools.price_from, which the cards used to read, never existed.
//
// Prices are compared in USD: each programme carries its own currency, and the
// caller supplies `toUsd` (live rates from money.js) so this stays pure and
// testable. The cheapest one is also kept in its own currency (fromAmount,
// fromCurrency), because that is the price the card shows. Free classes are not "from $0": a school whose only priced thing
// is free shows "Free classes" instead.

const DAY = 86400000;

/** Live = active, not a draft, not hidden, and not already over. */
const liveRetreat = (r, today) =>
  r.is_active && !r.is_draft && !r.is_hidden &&
  (isFlexible(r) ? !flexibleWindowOver(r, today) : (!r.end_date || r.end_date >= today));
const liveClass = (c, today) =>
  c.is_active !== false && !c.is_draft && (!c.end_date || c.end_date >= today);

/** Early-bird price while its deadline is in the future, else the list price. */
function retreatPrice(r, today) {
  const eb = Number(r.early_bird_price);
  if (eb > 0 && r.early_bird_deadline && r.early_bird_deadline >= today) return eb;
  return Number(r.price) || 0;
}

function retreatDays(r) {
  if (Number(r.duration_days) > 0) return Number(r.duration_days);
  if (r.start_date && r.end_date) {
    const d = Math.round((new Date(r.end_date) - new Date(r.start_date)) / DAY) + 1;
    return d > 0 ? d : null;
  }
  return null;
}

/**
 * @param {object} p
 * @param {Array} p.retreats   rows with school_id, price, early_bird_price, early_bird_deadline, currency, start_date, end_date, duration_days, is_active, is_draft, is_hidden
 * @param {Array} p.classes    rows with school_id, price, currency, is_free, is_active, is_draft, end_date
 * @param {(amount:number, currency:string)=>number} p.toUsd
 * @param {string} [p.today]   YYYY-MM-DD
 * @returns {Object<string, {fromUsd:number|null, fromAmount:number|null, fromCurrency:string|null, fromUnit:"person"|"class"|null, freeClasses:boolean,
 *           nextStart:string|null, minDays:number|null, maxDays:number|null, hasClasses:boolean, programmes:number, yttHours:number[]}>}
 */
export function summarizeProgrammes({ retreats = [], classes = [], toUsd, today = new Date().toISOString().slice(0, 10) }) {
  const out = {};
  const get = (id) => (out[id] = out[id] || { fromUsd: null, fromAmount: null, fromCurrency: null, fromUnit: null, freeClasses: false, nextStart: null, minDays: null, maxDays: null, hasClasses: false, programmes: 0, yttHours: [] });
  const offer = (s, amount, currency, unit) => {
    const usd = toUsd(amount, currency);
    if (!(usd > 0)) return;
    if (s.fromUsd == null || usd < s.fromUsd) { s.fromUsd = Math.round(usd); s.fromAmount = amount; s.fromCurrency = currency; s.fromUnit = unit; }
  };

  retreats.filter((r) => r.school_id && liveRetreat(r, today)).forEach((r) => {
    const s = get(r.school_id);
    s.programmes += 1;
    // Live Teacher Trainings, for the "Offers 200h YTT" badge on school cards.
    if (r.retreat_type === "teacher_training" && r.certification_hours && !s.yttHours.includes(r.certification_hours))
      s.yttHours = [...s.yttHours, r.certification_hours].sort((a, b) => a - b);
    offer(s, retreatPrice(r, today), r.currency || "USD", "person");
    // A flexible retreat "starts" whenever a guest can next arrive.
    const start = isFlexible(r) ? nextArrival(r, today) : r.start_date;
    if (start && start >= today && (!s.nextStart || start < s.nextStart)) s.nextStart = start;
    const d = retreatDays(r);
    if (d) { s.minDays = s.minDays == null ? d : Math.min(s.minDays, d); s.maxDays = s.maxDays == null ? d : Math.max(s.maxDays, d); }
  });

  classes.filter((c) => c.school_id && liveClass(c, today)).forEach((c) => {
    const s = get(c.school_id);
    s.programmes += 1;
    s.hasClasses = true;
    if (c.is_free || !(Number(c.price) > 0)) s.freeClasses = true;
    else offer(s, Number(c.price), c.currency || "USD", "class");
  });

  return out;
}

/** "23 days", "7–23 days", or null. */
export function durationLabel(s) {
  if (!s || s.minDays == null) return null;
  const unit = (n) => (n === 1 ? "day" : "days");
  return s.minDays === s.maxDays ? `${s.minDays} ${unit(s.minDays)}` : `${s.minDays}–${s.maxDays} days`;
}

/**
 * Schools with a price (or free classes) before "Price on request" ones. Within
 * each group the incoming order is kept, so any existing sort still applies.
 */
export function sortPricedFirst(schools, summary) {
  const priced = (s) => summary[s.id]?.fromUsd != null || !!summary[s.id]?.freeClasses;
  return schools
    .map((s, i) => ({ s, i, unpriced: priced(s) ? 0 : 1 }))
    .sort((a, b) => a.unpriced - b.unpriced || a.i - b.i)
    .map((x) => x.s);
}
